import React, { useEffect, useState } from "react";
import { CheckIcon, CloseIcon, PlusIcon, ShareIcon } from "@/components/icons";
import { installPlatform, shouldShowInstallBanner } from "./pushSupport";
import { isStandaloneDisplayMode } from "../loginCode";

const DISMISS_KEY = "touchpoint_portal_install_dismissed";

/**
 * DD-020: platform-native install affordance. Android gets a real install
 * button via beforeinstallprompt; iOS gets expandable numbered steps (its
 * only option — there is no install API); desktop/installed render nothing.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const IOS_STEPS: { icon: React.ReactNode; text: React.ReactNode }[] = [
  {
    icon: <ShareIcon className="w-4 h-4" />,
    text: (
      <>
        Tocca <span className="font-medium">Condividi</span> nella barra di
        Safari
      </>
    ),
  },
  {
    icon: <PlusIcon className="w-4 h-4" />,
    text: (
      <>
        Scorri e tocca{" "}
        <span className="font-medium">"Aggiungi alla schermata Home"</span>
      </>
    ),
  },
  {
    icon: <CheckIcon className="w-4 h-4" />,
    text: (
      <>
        Tocca <span className="font-medium">Aggiungi</span> in alto a destra
      </>
    ),
  },
];

export const InstallBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const platform = installPlatform(
    navigator.userAgent,
    isStandaloneDisplayMode()
  );
  const storedDismissal = Number(localStorage.getItem(DISMISS_KEY)) || null;

  if (
    dismissed ||
    !shouldShowInstallBanner(platform, storedDismissal, Date.now())
  ) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    if (choice.outcome === "accepted") {
      handleDismiss();
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mx-4 mt-4 flex items-start gap-3">
      <div className="flex-1 text-sm text-blue-900">
        <p className="font-medium mb-1">Installa l'app BeNerdy</p>
        {platform === "ios" ? (
          <>
            <p>
              Aggiungi l'app alla schermata Home per ricevere le notifiche sui
              nuovi contenuti.
            </p>
            {stepsOpen ? (
              <ol className="mt-3 space-y-2">
                {IOS_STEPS.map((step, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                      {step.icon}
                    </span>
                    <span>
                      {i + 1}. {step.text}
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <button
                type="button"
                onClick={() => setStepsOpen(true)}
                className="mt-2 text-blue-700 font-medium underline"
              >
                Mostrami come
              </button>
            )}
          </>
        ) : installPrompt ? (
          <>
            <p>Ricevi le notifiche sui nuovi contenuti da approvare.</p>
            <button
              type="button"
              onClick={handleInstall}
              className="mt-2 px-3 py-1.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              Installa
            </button>
          </>
        ) : (
          <p>
            Aggiungi l'app alla schermata Home per ricevere le notifiche sui
            nuovi contenuti.
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Chiudi"
        className="text-blue-400 hover:text-blue-600"
      >
        <CloseIcon className="w-4 h-4" />
      </button>
    </div>
  );
};
